import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { RxjsUtils } from '@app/core';
import { Subscription } from 'rxjs';
import { AuthenticationService, LoginResponseType } from '../services/authentication.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { filter, finalize, map, take, tap } from 'rxjs/operators';
import { NgIf } from '@angular/common';
import { MatCard, MatCardActions, MatCardContent, MatCardHeader, MatCardTitle } from '@angular/material/card';
import { MatError, MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { AntButtonModule } from '@antony/ng-ui';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  imports: [
    NgIf,
    MatCard,
    FormsModule,
    ReactiveFormsModule,
    MatCardHeader,
    MatCardTitle,
    MatCardContent,
    MatFormField,
    MatLabel,
    MatInput,
    MatIcon,
    MatSuffix,
    MatError,
    MatCardActions,
    AntButtonModule,
    RouterLink,
    TranslatePipe,
  ],
})
export class LoginComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private authService = inject(AuthenticationService);
  loginForm = new FormGroup({
    username: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
    password: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
  });

  subscriptions: Subscription[] = [];
  submitted = false;
  submitting = false;
  showPassword = false;
  labelHideBreakpoints: string[] = [];

  ngOnInit() {
    if (this.authService.isAuthenticated()) {
      this.router.navigate(['dashboard']);
    }

    this.checkForceRefresh();
  }

  ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  toggleVisibilityOfPassword() {
    this.showPassword = !this.showPassword;
  }

  submitForm(): void {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.submitted = true;
    this.submitting = true;
    const formValue = this.loginForm.value;

    this.authService
      .authenticate(formValue.username ?? '', formValue.password ?? '')
      .pipe(
        take(1),
        finalize(() => {
          this.checkForceRefresh();
        })
      )
      .subscribe((loginResult: LoginResponseType) => {
        this.submitting = false;
        let err = {};

        if (this.loginForm) {
          this.loginForm.enable();
        }

        switch (loginResult) {
          case 'success':
            const targetUri = new URLSearchParams(window.location.search).get('targetUri');

            if (targetUri) {
              void this.router.navigateByUrl(decodeURI(targetUri));
              return;
            }

            void this.router.navigate(['dashboard']);
            break;
          case 'invalid':
            err = { wrongCredentials: true };
            break;
          case 'error':
            break;
          case 'timeout':
            break;
          case 'disabled':
            err = { disabled: true };
            break;
          default:
            break;
        }

        this.loginForm.setErrors(err);
      });
  }

  private checkForceRefresh(): void {
    this.activatedRoute.queryParams
      .pipe(
        take(1),
        map((params): string => params['forceRefresh']),
        filter(RxjsUtils.isNotNullOrUndefined),
        tap(async (forceRefresh) => {
          // NOTE: queryParams are key-value pairs of strings
          if (forceRefresh === 'true') {
            await this.router.navigate([], {
              relativeTo: this.activatedRoute,
              queryParams: {},
            });
            window.location.reload();
          }
        })
      )
      .subscribe();
  }
}
